import { Person as PersonIcon } from "@mui/icons-material";
import DomainIcon from "@mui/icons-material/Domain";
import {
  Avatar as MUIAvatar,
  Divider,
  Fade,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { InlineLoading } from "../../../../common/components/Loading";
import { useActionRequest } from "../../../../common/hooks/api";
import { OmniBarContext } from "../../../../common/hooks/useOmniBar";
import { useSelf } from "../../../../common/hooks/useSelf";
import { selfActions } from "../../../../common/reducers/selfSlice";
import { Api } from "../../../../common/service/Api";
import { switchActiveOrganization } from "../../../util/organizationNavigation";
import { Error as RequestsError, Success as RequestsSuccess } from "../../../copy/requests";
import { useOrganization, usePrimaryOrganizationId } from "../../../hooks/useOrganization";
import { Routes } from "../../../root/routes";
import { ellipsis } from "../../../util/ellipsis";
import { BuildingOffice2Icon } from "@heroicons/react/24/outline";
import { clearEditModeFlag, shouldUseExtendedEditMode } from "../../../edit-mode/editModeState";

export const useMenu = (id) => {
  const [el, setEl] = React.useState(null);
  const isOpen = Boolean(el);
  const menuId = isOpen ? id : undefined;

  const click = (event) => {
    event.preventDefault();
    setEl(event.currentTarget);
  };

  const close = () => setEl(null);

  return {
    el,
    isOpen,
    id: menuId,
    click,
    close,
  };
};

const rejectKeyboardShortcut = (event: KeyboardEvent) =>
  event.target instanceof Node && isFormField(event.target);

const isFormField = (element: Node): boolean => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const name = element.nodeName.toLowerCase();
  const type = (element.getAttribute("type") || "").toLowerCase();
  return (
    name === "select" ||
    name === "textarea" ||
    (name === "input" &&
      type !== "submit" &&
      type !== "reset" &&
      type !== "checkbox" &&
      type !== "radio") ||
    element.isContentEditable
  );
};

const NavMenu = ({ menu, children, additionalClassNames = "", position = "right" }) => {
  return (
    <Menu
      className={`nav-popover-menu ${additionalClassNames}`}
      keepMounted
      TransitionComponent={Fade}
      id={menu.id}
      open={menu.isOpen}
      anchorEl={menu.el}
      onClose={menu.close}
      anchorOrigin={
        position === "bottom"
          ? {
              vertical: "bottom",
              horizontal: "left",
            }
          : {
              vertical: "center",
              horizontal: "right",
            }
      }
      transformOrigin={
        position === "bottom"
          ? {
              vertical: "top",
              horizontal: "left",
            }
          : {
              vertical: "center",
              horizontal: "left",
            }
      }
    >
      {children}
    </Menu>
  );
};

export const UserNavItem: React.FC<{
  horizontal?: boolean;
  useNewDesign?: boolean;
  settingsRoute?: string;
  userProfileRoute?: string;
}> = ({ horizontal = true, useNewDesign = false, settingsRoute, userProfileRoute }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const menu = useMenu("nav-profile-popover");
  const [self] = useSelf();
  const primaryOrgId = usePrimaryOrganizationId();
  const params = useParams() as any;
  const { openOmniBar } = useContext(OmniBarContext);

  const organizationId = params.organizationId || primaryOrgId;
  const [org] = useOrganization(organizationId);

  const { fetch: updateActiveOrganizationSelf, request: switchOrgRequest } = useActionRequest({
    api: switchActiveOrganization,
    messages: {
      defaultError: RequestsError.SetActiveOrganization,
      success: RequestsSuccess.SetActiveOrganization,
    },
    notifyError: true,
    notifySuccess: false,
  });

  const switchOrg = useCallback(
    async (e: any) => {
      e.preventDefault();
      openOmniBar({
        onClose: (data) => {
          if (data?.organization) {
            // On local/staging, don't reset the edit mode to make QA, etc. easier (POL-7467).
            if (!shouldUseExtendedEditMode()) {
              clearEditModeFlag();
            }
            updateActiveOrganizationSelf(data.organization);
          }
          menu.close();
        },
      });
    },
    [menu, openOmniBar, updateActiveOrganizationSelf],
  );

  const organizationSwitch = (
    <ListItemSecondaryAction className="user-menu__organization_switch">
      Switch
    </ListItemSecondaryAction>
  );

  useEffect(() => {
    const listener = (e) => {
      if (rejectKeyboardShortcut(e)) {
        return;
      }

      // '/' key with no modifiers
      if (e.keyCode == 191 && !e.metaKey && !e.shiftKey && !e.ctrlKey && !e.altKey) {
        switchOrg(e);
      }
    };

    document.addEventListener("keyup", listener);

    return () => document.removeEventListener("keyup", listener);
  }, [switchOrg]);

  const { fetch: logout } = useActionRequest({
    api: async () => await Api.login.logout(),
    onSuccess: () => {
      queryClient.invalidateQueries(["self"]);
      dispatch(selfActions.purge());
    },
  });

  const UserDisplayText = ({ useNewDesign = false }: { useNewDesign?: boolean }) => {
    if (useNewDesign && org) {
      return <span>{org.name}</span>;
    }
    // NOTE: display the organization name in non-production environments
    //       to provide useful context in videos attached to defects
    return org && window.location.hostname !== "polaris.truevault.com" ? (
      <div className="user-display-text" style={{ flexDirection: "row" }}>
        <div>
          {self.firstName} {self.lastName}
        </div>
        <div style={{ fontSize: ".7em", marginTop: "-5px", opacity: "0.5" }}>{org.name}</div>
      </div>
    ) : (
      <div className="user-display-text">
        {self.firstName} {self.lastName}
      </div>
    );
  };

  return (
    <>
      {useNewDesign ? (
        <div className="tw-boundary" onClick={menu.click} style={{ cursor: "pointer" }}>
          <div
            className={`flex items-center py-[8px] pr-[10px] pl-[10px] rounded-[8px] transition-all duration-150 ease-out active:scale-[0.98] ${
              menu.isOpen
                ? "bg-primary-100 text-primary-600"
                : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
            }`}
          >
            <BuildingOffice2Icon className="w-5 h-5 mr-[12px] shrink-0" />
            <span className="text-[14px] font-medium">
              <UserDisplayText useNewDesign={useNewDesign} />
            </span>
          </div>
        </div>
      ) : (
        <a className="primary-nav__bottom-link text-decoration-none" href="#" onClick={menu.click}>
          <PersonIcon className={`primary-nav__bottom-icon`} />
          <UserDisplayText useNewDesign={useNewDesign} />
        </a>
      )}

      <NavMenu
        menu={menu}
        additionalClassNames="user-menu"
        position={horizontal ? "right" : "bottom"}
      >
        <ListSubheader className="user-menu__name" disableGutters disableSticky>
          <Typography variant="inherit" noWrap>
            {ellipsis(`${self?.firstName} ${self?.lastName}`, 32)}
          </Typography>
        </ListSubheader>

        {org ? (
          <MenuItem onClick={switchOrg} disabled={switchOrgRequest.running}>
            <ListItemAvatar>
              <MUIAvatar
                variant="square"
                alt="organization icon"
                src={org?.faviconUrl?.toString()}
                style={{ width: "20px", height: "20px" }}
              >
                <DomainIcon style={{ width: "100%", height: "100%" }} />
              </MUIAvatar>
            </ListItemAvatar>
            <ListItemText disableTypography secondary={organizationSwitch}>
              <Typography variant="inherit" noWrap className="user-menu__organization_name">
                {org?.name}
              </Typography>
            </ListItemText>
          </MenuItem>
        ) : (
          <div className="text-center">
            <InlineLoading />
          </div>
        )}

        <Divider />
        {useNewDesign &&
          [
            settingsRoute && (
              <MenuItem key="settings" component={"a"} href={settingsRoute}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    style={{ width: "20px", height: "20px", color: "currentColor" }}
                  >
                    <path
                      fillRule="evenodd"
                      d="M11.828 2.25c-.916 0-1.699.663-1.85 1.567l-.091.549a.798.798 0 0 1-.517.608 7.45 7.45 0 0 0-.478.198.798.798 0 0 1-.796-.064l-.453-.324a1.875 1.875 0 0 0-2.416.2l-.243.243a1.875 1.875 0 0 0-.2 2.416l.324.453a.798.798 0 0 1 .064.796 7.448 7.448 0 0 0-.198.478.798.798 0 0 1-.608.517l-.55.092a1.875 1.875 0 0 0-1.566 1.849v.344c0 .916.663 1.699 1.567 1.85l.549.091c.281.047.508.25.608.517.06.162.127.321.198.478a.798.798 0 0 1-.064.796l-.324.453a1.875 1.875 0 0 0 .2 2.416l.243.243c.648.648 1.67.733 2.416.2l.453-.324a.798.798 0 0 1 .796-.064c.157.071.316.137.478.198.267.1.47.327.517.608l.092.55c.15.903.932 1.566 1.849 1.566h.344c.916 0 1.699-.663 1.85-1.567l.091-.549a.798.798 0 0 1 .517-.608 7.52 7.52 0 0 0 .478-.198.798.798 0 0 1 .796.064l.453.324a1.875 1.875 0 0 0 2.416-.2l.243-.243c.648-.648.733-1.67.2-2.416l-.324-.453a.798.798 0 0 1-.064-.796c.071-.157.137-.316.198-.478.1-.267.327-.47.608-.517l.55-.091a1.875 1.875 0 0 0 1.566-1.85v-.344c0-.916-.663-1.699-1.567-1.85l-.549-.091a.798.798 0 0 1-.608-.517 7.507 7.507 0 0 0-.198-.478.798.798 0 0 1 .064-.796l.324-.453a1.875 1.875 0 0 0-.2-2.416l-.243-.243a1.875 1.875 0 0 0-2.416-.2l-.453.324a.798.798 0 0 1-.796.064 7.462 7.462 0 0 0-.478-.198.798.798 0 0 1-.517-.608l-.091-.55a1.875 1.875 0 0 0-1.85-1.566h-.344ZM12 15.75a3.75 3.75 0 1 0 0-7.5 3.75 3.75 0 0 0 0 7.5Z"
                      clipRule="evenodd"
                    />
                  </svg>

                  <div style={{ marginLeft: "10px" }}>Account Settings</div>
                </div>
              </MenuItem>
            ),
            userProfileRoute && (
              <MenuItem key="profile" component={"a"} href={userProfileRoute}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ width: "20px", height: "20px", color: "currentColor" }}
                >
                  <path
                    fillRule="evenodd"
                    d="M4.5 3.75a3 3 0 0 0-3 3v10.5a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V6.75a3 3 0 0 0-3-3h-15Zm4.125 3a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Zm-3.873 8.703a4.126 4.126 0 0 1 7.746 0 .75.75 0 0 1-.351.92 7.47 7.47 0 0 1-3.522.877 7.47 7.47 0 0 1-3.522-.877.75.75 0 0 1-.351-.92ZM15 8.25a.75.75 0 0 0 0 1.5h3.75a.75.75 0 0 0 0-1.5H15ZM14.25 12a.75.75 0 0 1 .75-.75h3.75a.75.75 0 0 1 0 1.5H15a.75.75 0 0 1-.75-.75Zm.75 2.25a.75.75 0 0 0 0 1.5h3.75a.75.75 0 0 0 0-1.5H15Z"
                    clipRule="evenodd"
                  />
                </svg>

                <div style={{ marginLeft: "10px" }}> My Profile</div>
              </MenuItem>
            ),
            <MenuItem key="help" component={"div"} onClick={() => (window as any).Beacon("open")}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ width: "20px", height: "20px", color: "currentColor" }}
                >
                  <path
                    fillRule="evenodd"
                    d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 0 1-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 0 1-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 0 1-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584ZM12 18a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                    clipRule="evenodd"
                  />
                </svg>

                <div style={{ marginLeft: "10px" }}>Help</div>
              </div>
            </MenuItem>,
          ].filter(Boolean)}

        {self.adminSite && <Divider />}
        {self.adminSite && (
          <MenuItem component={"a"} href={self.adminSite}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                style={{ width: "20px", height: "20px", color: "currentColor" }}
              >
                <path
                  fillRule="evenodd"
                  d="M2.25 5.25a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3V15a3 3 0 0 1-3 3h-3v.257c0 .597.237 1.17.659 1.591l.621.622a.75.75 0 0 1-.53 1.28h-9a.75.75 0 0 1-.53-1.28l.621-.622a2.25 2.25 0 0 0 .659-1.59V18h-3a3 3 0 0 1-3-3V5.25Zm1.5 0v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5Z"
                  clipRule="evenodd"
                />
              </svg>

              <div style={{ marginLeft: "10px" }}>Admin</div>
            </div>
          </MenuItem>
        )}
        {self.adminSite && (
          <MenuItem component={"a"} href={Routes.admin.csComplianceDashboard}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                style={{ width: "20px", height: "20px", color: "currentColor" }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5"
                />
              </svg>

              <div style={{ marginLeft: "10px" }}>CS Dashboard</div>
            </div>
          </MenuItem>
        )}

        <Divider />
        <MenuItem component={"a"} onClick={logout}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ width: "20px", height: "20px", color: "currentColor" }}
            >
              <path
                fillRule="evenodd"
                d="M16.5 3.75a1.5 1.5 0 0 1 1.5 1.5v13.5a1.5 1.5 0 0 1-1.5 1.5h-6a1.5 1.5 0 0 1-1.5-1.5V15a.75.75 0 0 0-1.5 0v3.75a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3V5.25a3 3 0 0 0-3-3h-6a3 3 0 0 0-3 3V9A.75.75 0 1 0 9 9V5.25a1.5 1.5 0 0 1 1.5-1.5h6Zm-5.03 4.72a.75.75 0 0 0 0 1.06l1.72 1.72H2.25a.75.75 0 0 0 0 1.5h10.94l-1.72 1.72a.75.75 0 1 0 1.06 1.06l3-3a.75.75 0 0 0 0-1.06l-3-3a.75.75 0 0 0-1.06 0Z"
                clipRule="evenodd"
              />
            </svg>

            <div style={{ marginLeft: "10px" }}>Logout</div>
          </div>
        </MenuItem>
      </NavMenu>
    </>
  );
};
